import { useGetFrameworksListQuery } from '@/modules/agent/requests';
import ZAvatarGradient from '@/shared/components/avatarGradient';
import type { ZTableColumnProps } from '@/shared/components/table';
import { Chip } from '@nextui-org/react';
import dayjs from 'dayjs';
import { agentSessionStatusLabelMap, frameworkTypeLabels } from '../constants';
import type { AgentSession, AgentSessions, CustomGapAgentSession, PolicyAgentSession, QaAgentSession } from '../types';
import { AgentSessionStatus, AgentSessionStepType, AgentSessionType } from '../types';


const baseColumns = (memberId: string | undefined): ZTableColumnProps<any>[] => [
  {
    type: "custom",
    fieldName: "name",
    headerName: "Session Title",
    headerClassName: "w-auto w-1/3 min-w-[200px]",
    sortable: true,
    renderCell: ({ name, permissions }) => (
      <div className="text-[#333] flex gap-2 break-all items-center min-w-[200px]">
        {name ?? ""}
        {permissions && permissions.owner !== memberId && (
          <span className="h-3 flex items-center justify-center text-ellipsis overflow-hidden rounded border-1 border-[#006FEE] bg-[#006FEE33] p-1 text-[8px] leading-3 text-[#006FEE] shrink-0">
            SHARED
          </span>
        )}
      </div>
    )
  },
  {
    type: "custom",
    fieldName: "status_level",
    headerName: "Status",
    sortable: true,
    headerClassName: "min-w-[250px] flex-grow-1",
    renderCell: ({ status }: any) => {
      return (
        <div className="flex items-center gap-3">
          <Chip
            variant="bordered"
            radius="full"
            className="h-6 border-1.5"
            classNames={{
              content: "px-1 py-2 flex items-center gap-1",
            }}
            style={{
              color: agentSessionStatusLabelMap[status]?.borderColor ?? "black",
              borderColor: agentSessionStatusLabelMap[status]?.borderColor ?? "black",
              backgroundColor: agentSessionStatusLabelMap[status]?.backgroundColor ?? "transparent",
            }}
          >
            {agentSessionStatusLabelMap[status]?.renderIcon?.()}
          </Chip>
          <div className="text-[#333]">
            {agentSessionStatusLabelMap[status]?.label ?? "N/A"}
          </div>
        </div>
      );
    },
  },
  {
    type: "custom",
    fieldName: "updated_at",
    headerName: "Last Updated",
    sortable: true,
    getSortValue: ({ updated_at }) => dayjs(updated_at).isValid() ? dayjs(updated_at).toDate().getTime() : 0,
    renderCell: ({ updated_at }) => {
      if (dayjs(updated_at).isValid()) {
        const parsedDate = dayjs.utc(updated_at);
        const localDate = parsedDate.local();
        return <div className="text-[#333]">{localDate.fromNow()}</div>;
      }
      return "";
    },
    headerClassName: "min-w-[120px]"
  },
  {
    type: "custom",
    fieldName: "created_by",
    headerName: "Created By",
    renderCell: ({ created_by }) => {
      return created_by ?  <div className="flex items-center gap-3">
        <ZAvatarGradient name={created_by as string} size={24} textSize={10}/>
        <p className="font-medium">{created_by}</p>
      </div> : "N/A"
    },
    sortable: true,
    headerClassName: "min-w-[180px]"
  },

];

const RenderFrameworkColumn = ({session}: {session : CustomGapAgentSession}) => {
  const { data: customFrameworksList } = useGetFrameworksListQuery({ isEnabled : true })

  return <div className="text-[#333]">{customFrameworksList?.mapping?.[session?.gap_custom_framework.framework_id as string] || "N/A"}</div>
}

const getSessionTypeSpecificColumn = (type: AgentSessionType): ZTableColumnProps<AgentSession> => {
  switch (type) {
    case AgentSessionType.RISK_ASSESSMENT:
    case AgentSessionType.SOC2_GAP:
    case AgentSessionType.DORA_GAP:
      return {
        type: "custom",
        fieldName: "session.type",
        headerName: "Framework",
        sortable: true,
        headerClassName: 'min-w-[180px] flex-grow-1',
        renderCell: (session) => {

          if(session.type === AgentSessionType.GAP_CUSTOM_FRAMEWORK){
            return <RenderFrameworkColumn session={session} />
          }
          return <div className="text-[#333]">{frameworkTypeLabels[session.type] || 'N/A'}</div>

        }
      };
    case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
      return {
        type: "custom",
        fieldName: "session.multi_file_vendor_assessment.vendor_name",
        headerName: "Vendor Product",
        headerClassName: "w-1/4 min-w-[220px]",
        sortable: true,
        renderCell: (session: any ) => (
          <div className="text-[#333] flex gap-[6px] items-center">
              {(session.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
              ? `${session.multi_file_vendor_assessment?.vendor_name} 
              ${session.multi_file_vendor_assessment?.product_name || ''}`
              : 'N/A') || 'N/A'}
          </div>
        )
      };
    case AgentSessionType.QA_FILLER:
      return {
        type: "custom",
        fieldName: "session.qa_filler.customer_name",
        headerName: "Customer Name",
        headerClassName: "w-1/4 min-w-[250px]",
        sortable: true,
        renderCell: (session : any) => (
          <div className="text-[#333]">{(session as QaAgentSession)?.qa_filler?.customer_name as string || 'N/A'}</div>
        )
      };
    case AgentSessionType.POLICY_MANAGEMENT:
      return {
        type: "custom",
        fieldName: "session.policy_management.documentation_name",
        headerName: "Documentation Name",
        sortable: true,
        renderCell: (session : any) => (
          <div className="text-[#333]">
            {(session.type === AgentSessionType.POLICY_MANAGEMENT
              ? (session as PolicyAgentSession).policy_management?.documentation_name
              : 'N/A') || 'N/A'}
          </div>
        )
      };
    default:
      return {
        type: "custom",
        fieldName: "framework",
        headerName: "Framework",
        sortable: true,
        renderCell: () => <div className="text-[#333]">N/A</div>
      };
  }
};

export const getSessionColumns = (sessionType: AgentSessionType, memberId: string | undefined): ZTableColumnProps<AgentSessions>[] => {
  const specificColumn = getSessionTypeSpecificColumn(sessionType);
  const columns = [...baseColumns(memberId)];
  columns.splice(1, 0, specificColumn);
  return columns;
};

export const getSessionStatusLevel = (status: string) => {
  switch (status) {
    case AgentSessionStatus.IN_PROGRESS:
      return 0;
    case AgentSessionStatus.FORM_SENT:
      return 1;
    case AgentSessionStatus.INPUT_NEEDED:
      return 2;
    case AgentSessionStatus.UNDER_REASSESSMENT:
      return 3;
    case AgentSessionStatus.COMPLETE:
      return 4;
    default:
      return 4;
  }
}